import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDocumentMetadata } from '../hooks/useDocumentMetadata';
import { PORTFOLIO_ITEMS } from '../lib/constants';
import { Button } from '../components/ui/Button';
import styles from './ProjectDetailPage.module.css';

export const ProjectDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language === 'en' ? 'en' : 'it') as 'it' | 'en';

  // Find corresponding portfolio item
  const project = PORTFOLIO_ITEMS.find((item) => item.id === id);

  useDocumentMetadata(
    project ? `${project.title[lang]} | Case Study NexTrainE` : "Case Study | NexTrainE",
    project ? project.desc[lang] : undefined
  );

  if (!project) {
    return (
      <div className={styles.notFoundPage}>
        <div className={styles.notFoundContent}>
          <h1 className={styles.notFoundTitle}>{t('project.not_found')}</h1>
          <p className={styles.notFoundDesc}>{t('project.not_found_desc')}</p>
          <Button variant="secondary" href="/portfolio">
            {t('project.back_to_portfolio')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* ─── Hero Header (Dark Theme) ─── */}
      <section className={styles.hero}>
        <div className={styles.heroGlow} />
        <div className="container">
          <div className={styles.heroContent}>
            <Link to="/portfolio" className={styles.backLink}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {t('project.back_to_portfolio')}
            </Link>

            <div className={styles.category}>
              <span className={styles.categoryDot} />
              {project.category[lang]}
            </div>

            <h1 className={styles.title}>{project.title[lang]}</h1>

            <div className={styles.tags}>
              {project.tags.map((tag) => (
                <span key={tag} className={styles.tag}>
                  {tag}
                </span>
              ))}
            </div>

            <div className={styles.metaGrid}>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>{t('project.status_label')}</span>
                <span className={styles.metaValue}>
                  <span className={styles.statusBadge}>{project.status[lang]}</span>
                </span>
              </div>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>{t('project.client_label')}</span>
                <span className={styles.metaValue}>{project.client[lang]}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Stats Grid Section ─── */}
      {project.stats && project.stats.length > 0 && (
        <section className={styles.statsWrapper}>
          <div className="container">
            <div className={styles.statsGrid}>
              {project.stats.map((stat, idx) => (
                <div key={idx} className={styles.statCard}>
                  <div className={styles.statValue}>{stat.value}</div>
                  <div className={styles.statLabel}>{stat.label[lang]}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── Main Content Details ─── */}
      <section className={styles.detailsSection}>
        <div className="container">
          <div className={styles.contentContainer}>
            
            {/* Challenge & Solution Grid */}
            <div className={styles.grid2Col}>
              
              {/* Challenge Card */}
              {project.challenge && (
                <div className={styles.sectionCard}>
                  <div className={styles.sectionHeader}>
                    <span className={styles.sectionIcon}>
                      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </span>
                    <h3 className={styles.sectionTitle}>{t('project.challenge')}</h3>
                  </div>
                  <p className={styles.sectionText}>{project.challenge[lang]}</p>
                </div>
              )}

              {/* Solution Card */}
              {project.solution && (
                <div className={styles.sectionCard}>
                  <div className={styles.sectionHeader}>
                    <span className={styles.sectionIcon}>
                      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </span>
                    <h3 className={styles.sectionTitle}>{t('project.solution')}</h3>
                  </div>
                  <p className={styles.sectionText}>{project.solution[lang]}</p>
                </div>
              )}
            </div>

            {/* Results & Impact full width Card */}
            {project.impact && (
              <div className={styles.fullWidthCard}>
                <div className={styles.sectionHeader}>
                  <span className={styles.sectionIcon}>
                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                  </span>
                  <h3 className={styles.sectionTitle}>{t('project.impact')}</h3>
                </div>
                <p className={styles.sectionText}>{project.impact[lang]}</p>
              </div>
            )}

            {/* CTA Section */}
            <div className={styles.ctaCard}>
              <div className={styles.ctaGlow} />
              <h3 className={styles.ctaTitle}>{t('project.cta_title')}</h3>
              <p className={styles.ctaDesc}>{t('project.cta_desc')}</p>
              <div className={styles.ctaActions}>
                <Button variant="primary" href="/#contact">
                  {t('project.cta_btn')}
                </Button>
                <Button variant="secondary" href="/portfolio">
                  {t('project.back_to_portfolio')}
                </Button>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};
